import React, { useState, useEffect } from 'react';
import { BusinessIdea } from '../../services/businessIdeaTypes';
import { supabaseHelper } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

interface SavedIdeasListProps {
  ideas?: BusinessIdea[];
  selectedIdea?: BusinessIdea | null;
  onSelectIdea: (idea: BusinessIdea) => void;
}

const SavedIdeasList: React.FC<SavedIdeasListProps> = ({ ideas, onSelectIdea, selectedIdea }) => {
  const [savedIdeas, setSavedIdeas] = useState<BusinessIdea[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedIdeas, setExpandedIdeas] = useState<Record<string, boolean>>({});

  useEffect(() => {
    // If ideas are provided as props, use them directly
    if (ideas) {
      setSavedIdeas(ideas);
      setIsLoading(false);
      return;
    }
    
    // Otherwise fetch from Supabase
    const fetchIdeas = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const fetchedIdeas = await supabaseHelper.getIdeas();
        setSavedIdeas(fetchedIdeas || []);
      } catch (err) {
        console.error('Error fetching ideas:', err);
        setError('Failed to load saved ideas');
        toast.error('Failed to load saved ideas');
      } finally {
        setIsLoading(false);
      }
    };

    fetchIdeas();
  }, [ideas]);

  const filteredIdeas = savedIdeas.filter(idea =>
    idea.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    idea.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown date';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCost = (cost?: number) => {
    if (typeof cost !== 'number') return '';
    return cost.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const toggleIdeaExpansion = (ideaId: string, event: React.MouseEvent) => {
    // Prevent the idea selection when clicking the expand/collapse button
    event.stopPropagation();
    setExpandedIdeas(prev => ({
      ...prev,
      [ideaId]: !prev[ideaId]
    }));
  };

  return (
    <div className="saved-ideas-list p-6 w-full">
      <h2 className="text-xl font-semibold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Saved Ideas</h2>
      
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search saved ideas..."
          className="w-full px-4 py-2 bg-white/60 backdrop-blur-sm border border-white/40 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-300"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="text-center text-gray-500 py-8">
          Loading saved ideas...
        </div>
      ) : error ? (
        <div className="text-center text-red-500 py-8">
          {error}
        </div>
      ) : filteredIdeas.length === 0 ? (
        <div className="text-center text-gray-500 bg-white/40 backdrop-blur-sm p-6 rounded-lg shadow-sm border border-white/30">
          {searchTerm ? 'No matching ideas found' : 'No saved ideas yet'}
        </div>
      ) : (
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1 w-full">
          {filteredIdeas.map((idea) => (
            <div
              key={idea.id}
              className={`relative overflow-hidden rounded-lg cursor-pointer transition-all duration-300 ${
                selectedIdea?.id === idea.id
                  ? 'bg-blue-50/70 backdrop-blur-sm border border-blue-300/50 shadow-md'
                  : 'bg-white/40 backdrop-blur-sm hover:bg-white/60 border border-white/40 shadow-sm hover:shadow'
              }`}
              onClick={() => onSelectIdea(idea)}
            >
              <div className={`absolute inset-0 bg-gradient-to-r ${
                selectedIdea?.id === idea.id
                  ? 'from-blue-400/10 to-indigo-400/10'
                  : 'from-gray-200/5 to-gray-100/5'
              }`}></div>
              <div className="relative z-10 p-3 flex justify-between items-center">
                <h3 className={`text-lg font-semibold ${
                  selectedIdea?.id === idea.id
                    ? 'text-blue-700'
                    : 'text-gray-700'
                } break-words pr-2 flex-1`}>{idea.title}</h3>
                <button 
                  className="text-gray-500 hover:text-blue-600 focus:outline-none transition-colors duration-300"
                  onClick={(e) => toggleIdeaExpansion(idea.id || '', e)}
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className={`h-5 w-5 transition-transform duration-300 ${expandedIdeas[idea.id || ''] ? 'transform rotate-180' : ''}`} 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
              
              {expandedIdeas[idea.id || ''] && (
                <div className="relative z-10 px-3 pb-3 pt-1 border-t border-white/30">
                  <p className="text-sm text-gray-600 mb-2">{idea.description}</p>
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span className="bg-white/50 px-2 py-1 rounded-md">Created: {formatDate(idea.createdAt)}</span>
                    {idea.cost && (
                      <span className="font-medium bg-white/50 px-2 py-1 rounded-md">
                        Estimated Cost: {formatCost(idea.cost)}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SavedIdeasList;
